import React, { useState, useMemo, useEffect, useTransition } from 'react';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useMutation, ApolloError } from '@apollo/client';

import { GET_USER } from '@/shared/queries';
import {
  CREATE_USER,
  UPDATE_CLIENT,
  UPDATE_STYLIST,
  UPDATE_USER_ADDRESS,
} from '@/shared/mutations';
import {
  MutationUpdateStylistArgs,
  MutationCreateUserArgs,
  User,
  MutationResult,
  MutationUpdateClientArgs,
  MutationUpdateUserAddressArgs,
  Gender,
  Country,
} from '@/shared/types/generated';
import {
  BioData,
  GalleryImages,
  SavedFormValues,
  ProfileFormDataWithoutCheckBox,
  ClientMeasurements,
} from '@/shared/types/stylist';
import { useAuth } from '@/services/auth/AuthProvider';
import SentryService from '@/services/sentry/Sentry.service';
import { getInitialValues, galleryImagesEqual } from './helpers';
import { arraysEqual, shouldSurfaceGraphQLError } from '@/shared/utils';
import { mapLocationTypeToEnum, mapEnumToLocationType } from './addressMappers';
import BasicInfoSection from './BasicInfo';
import AboutYouSection from './About';
import ExpertiseSection from './Expertise';
import PortfolioSection from './Portfolio';
import ClientAesthetics from './ClientAesthetics';
import MeasurementsSection from './MeasurementsSection';
import NotificationSection from './NotificationSection';
import InPersonSessionSection from './InPersonSessionSection';
import AddressForm, { AddressFormData } from './Address';
import Button from '@/shared/components/Button';
import {
  Container,
  FormContainer,
  FormSection,
  SectionHeading,
  DescriptionText,
  IntroContentContainer,
  StyledBodyText,
  PageTitle,
  FlexGroup,
  ButtonWrapper,
  RequiredText,
} from '@/shared/styles/styledComponents/editProfileForm';
import { openImagePicker } from '@/shared/utils';
import { ButtonSelectItem } from '@/shared/styles/types';
import useStore from '@/stores/useStore';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { useScrollToTop } from '@/shared/hooks/useScrollToTop';
import { useImageUpload } from '@/shared/hooks/useImageUpload';
import { ROUTES } from '@/shared/constants/routes';
import { useRouter } from 'expo-router';
import { IS_WEB } from '@/shared/constants/utility';
import {
  BOTTOM_TAB_BAR_HEIGHT,
  ASPECT_RATIOS,
} from '@/shared/styles/constants';
import BackToTopButton from '@/shared/components/BackToTopButton';

interface Props {
  user: User;
}

function hasAddressChanged(
  userAddress: User['address'],
  formData: AddressFormData,
): boolean {
  const streetAddress = [formData.addressLine1, formData.addressLine2]
    .filter(Boolean)
    .join('\n');

  return (
    userAddress?.city !== formData.city ||
    userAddress?.state !== formData.state ||
    userAddress?.zipCode !== formData.zipCode ||
    userAddress?.streetAddress !== streetAddress ||
    userAddress?.locationType !==
      mapLocationTypeToEnum(formData.locationType) ||
    userAddress?.accessDetails !== formData.accessDetails
  );
}

const EditProfileForm: React.FC<Props> = ({ user }) => {
  const { isStylist } = useStore();
  const router = useRouter();
  const { isDesktop } = useBreakpoint();
  const { setSnackbarState } = useStore();
  const { getAccessToken, auth0User } = useAuth();
  const scrollToTop = useScrollToTop();
  const { isUploading, uploadMultipleImages } = useImageUpload();
  const [, startTransition] = useTransition();

  const initialData = getInitialValues(user, isStylist);

  const [savedValues, setSavedValues] = useState<SavedFormValues>(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [profileFormData, setProfileFormData] =
    useState<ProfileFormDataWithoutCheckBox>(initialData.profileFormData);
  const [bioData, setBioData] = useState<BioData>(initialData.bioData);
  const [clientMeasurements, setClientMeasurements] =
    useState<ClientMeasurements>(initialData.clientMeasurements);
  const [selectedExpertise, setSelectedExpertise] = useState<
    ButtonSelectItem[]
  >(initialData.selectedExpertise as ButtonSelectItem[]);
  const [selectedStylingPreference, setSelectedStylingPreference] = useState<
    ButtonSelectItem[]
  >(initialData.selectedStylingPreference as ButtonSelectItem[]);
  const [galleryImages, setGalleryImages] = useState<GalleryImages>(
    initialData.galleryImages,
  );
  const [addressData, setAddressData] = useState<AddressFormData>({
    city: user.address?.city || '',
    state: user.address?.state || '',
    locationType: mapEnumToLocationType(user.address?.locationType),
    addressLine1: user.address?.streetAddress?.split('\n')[0] || '',
    addressLine2: user.address?.streetAddress?.split('\n')[1] || '',
    zipCode: user.address?.zipCode || '',
    accessDetails: user.address?.accessDetails || '',
  });

  // Update local state when user prop changes (after refetch)
  // TODO(ES-205): implement pattern that doesn't rely on useEffect for updating local state
  useEffect(() => {
    const updatedValues = getInitialValues(user, isStylist);

    // Batch all state updates together to minimize rerenders
    startTransition(() => {
      setProfileFormData(updatedValues.profileFormData);
      setBioData(updatedValues.bioData);
      setClientMeasurements(updatedValues.clientMeasurements);
      setSelectedExpertise(
        updatedValues.selectedExpertise as ButtonSelectItem[],
      );
      setSelectedStylingPreference(
        updatedValues.selectedStylingPreference as ButtonSelectItem[],
      );
      setGalleryImages(updatedValues.galleryImages);
      setSavedValues(updatedValues);
      setAddressData({
        city: user.address?.city || '',
        state: user.address?.state || '',
        locationType: mapEnumToLocationType(user.address?.locationType),
        addressLine1: user.address?.streetAddress?.split('\n')[0] || '',
        addressLine2: user.address?.streetAddress?.split('\n')[1] || '',
        zipCode: user.address?.zipCode || '',
        accessDetails: user.address?.accessDetails || '',
      });
    });
  }, [isStylist, user]);

  const onUpdateCompleted = () => {
    // Don't update state here - will be updated via useEffect when user prop changes
    scrollToTop();
    setSnackbarState({
      open: true,
      message: 'Update profile successful',
      severity: 'success',
    });
  };

  const [createUser] = useMutation<
    MutationResult<'createUser'>,
    MutationCreateUserArgs
  >(CREATE_USER);

  const [updateStylist] = useMutation<
    MutationResult<'updateStylist'>,
    MutationUpdateStylistArgs
  >(UPDATE_STYLIST, {
    refetchQueries: [{ query: GET_USER }],
    onCompleted: onUpdateCompleted,
  });

  const [updateClient] = useMutation<
    MutationResult<'updateClient'>,
    MutationUpdateClientArgs
  >(UPDATE_CLIENT, {
    refetchQueries: [{ query: GET_USER }],
    onCompleted: onUpdateCompleted,
  });

  const [updateUserAddress] = useMutation<
    MutationResult<'updateUserAddress'>,
    MutationUpdateUserAddressArgs
  >(UPDATE_USER_ADDRESS, {
    refetchQueries: [{ query: GET_USER }],
  });

  const isFormValid = useMemo(() => {
    // Check if all required fields are filled
    const isBasicInfoValid =
      profileFormData.firstName?.trim() !== '' &&
      profileFormData.lastName?.trim() !== '';
    const isPhoneValid =
      !profileFormData.phoneNumber ||
      isValidPhoneNumber(`+1${profileFormData.phoneNumber}`, 'US');

    const isStylingPreferenceValid = selectedStylingPreference.length > 0;

    if (isStylist) {
      const isBioValid = bioData.bio?.trim() !== '';
      const isExpertiseValid = selectedExpertise.length > 0;

      return (
        isBasicInfoValid &&
        isPhoneValid &&
        isBioValid &&
        isExpertiseValid &&
        isStylingPreferenceValid
      );
    }

    return isBasicInfoValid && isPhoneValid && isStylingPreferenceValid;
  }, [
    bioData,
    isStylist,
    profileFormData,
    selectedExpertise,
    selectedStylingPreference,
  ]);

  const isChanged = useMemo(() => {
    const addressChanged = hasAddressChanged(user.address, addressData);

    return (
      savedValues.profileFormData.profileImage !==
        profileFormData.profileImage ||
      savedValues.profileFormData.firstName !== profileFormData.firstName ||
      savedValues.profileFormData.lastName !== profileFormData.lastName ||
      savedValues.profileFormData.gender !== profileFormData.gender ||
      savedValues.profileFormData.birthDate?.getTime() !==
        profileFormData.birthDate?.getTime() ||
      savedValues.profileFormData.phoneNumber !== profileFormData.phoneNumber ||
      savedValues.profileFormData.shareAge !== profileFormData.shareAge ||
      savedValues.bioData.bio !== bioData.bio ||
      savedValues.bioData.tikTok !== bioData.tikTok ||
      savedValues.bioData.instagram !== bioData.instagram ||
      savedValues.bioData.substack !== bioData.substack ||
      savedValues.clientMeasurements.sizingDetails !==
        clientMeasurements.sizingDetails ||
      savedValues.clientMeasurements.height !== clientMeasurements.height ||
      !arraysEqual(savedValues.selectedExpertise, selectedExpertise) ||
      !arraysEqual(
        savedValues.selectedStylingPreference,
        selectedStylingPreference,
      ) ||
      !galleryImagesEqual(savedValues.galleryImages, galleryImages) ||
      addressChanged
    );
  }, [
    profileFormData,
    bioData,
    clientMeasurements,
    selectedExpertise,
    selectedStylingPreference,
    galleryImages,
    savedValues,
    addressData,
    user.address,
  ]);

  const selectImage = async () => {
    try {
      const profileImage = await openImagePicker(ASPECT_RATIOS.SQUARE);
      if (profileImage) {
        setProfileFormData((prev) => ({ ...prev, profileImage }));
      }
    } catch (error) {
      setSnackbarState({
        open: true,
        message:
          error instanceof Error ? error.message : 'Failed to select image',
        severity: 'error',
      });
    }
  };

  const selectLandscapeImage = async () => {
    try {
      const image = await openImagePicker(ASPECT_RATIOS.LANDSCAPE);
      if (image) {
        setGalleryImages((prev) => ({ ...prev, landscapeImage: image }));
      }
    } catch (error) {
      setSnackbarState({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : 'Error using selected image, please try again.',
        severity: 'error',
      });
    }
  };

  const selectPortraitImage = async (index: number) => {
    try {
      const image = await openImagePicker(ASPECT_RATIOS.PORTRAIT);
      if (image) {
        setGalleryImages((prev) => {
          const newPortraitImages = [...prev.portraitImages];
          newPortraitImages[index] = image;
          return { ...prev, portraitImages: newPortraitImages };
        });
      }
    } catch (error) {
      setSnackbarState({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : 'Error using selected image, please try again.',
        severity: 'error',
      });
    }
  };

  const addPortraitImage = async () => {
    try {
      const image = await openImagePicker(ASPECT_RATIOS.PORTRAIT);
      if (image) {
        setGalleryImages((prev) => ({
          ...prev,
          portraitImages: [...prev.portraitImages, image],
        }));
      }
    } catch (error) {
      setSnackbarState({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : 'Error using selected image, please try again.',
        severity: 'error',
      });
    }
  };

  const getChangedImages = () => {
    const changes: { [key: string]: { newImage: string; oldImage?: string } } =
      {};

    // Check profile image
    if (
      profileFormData.profileImage !== savedValues.profileFormData.profileImage
    ) {
      changes.profileImage = {
        newImage: profileFormData.profileImage,
        oldImage: savedValues.profileFormData.profileImage,
      };
    }

    // Check landscape image
    if (
      galleryImages.landscapeImage !== savedValues.galleryImages.landscapeImage
    ) {
      changes.landscapeImage = {
        newImage: galleryImages.landscapeImage || '',
        oldImage: savedValues.galleryImages.landscapeImage || '',
      };
    }

    // Check portrait images
    const maxPortraitImages = Math.max(
      galleryImages.portraitImages.length,
      savedValues.galleryImages.portraitImages.length,
    );

    for (let i = 0; i < maxPortraitImages; i++) {
      const newImage = galleryImages.portraitImages[i] || '';
      const oldImage = savedValues.galleryImages.portraitImages[i] || '';

      if (newImage !== oldImage) {
        changes[`portraitImage${i}`] = {
          newImage,
          oldImage,
        };
      }
    }

    return changes;
  };

  const saveProfile = async () => {
    if (!isFormValid) {
      return;
    }

    setIsLoading(true);

    try {
      const phoneNumber = Boolean(profileFormData.phoneNumber)
        ? `+1${profileFormData.phoneNumber}`
        : null;
      const token = await getAccessToken();
      const changedImages = getChangedImages();

      const uploadedImages = await uploadMultipleImages(token, changedImages);

      const profileImageUrl =
        uploadedImages.profileImage || profileFormData.profileImage;

      const landscapeImageUrl =
        uploadedImages.landscapeImage || galleryImages.landscapeImage;

      const portraitImageUrls = galleryImages.portraitImages
        .map((image, index) => uploadedImages[`portraitImage${index}`] || image)
        .filter(Boolean);

      await createUser({
        variables: {
          input: {
            auth0Id: user.auth0Id ?? auth0User?.sub,
            email: user.email,
            firstName: profileFormData.firstName,
            lastName: profileFormData.lastName,
            gender: profileFormData.gender ?? Gender.PreferNotToSay,
            birthDate: profileFormData.birthDate,
            profilePicUrl: profileImageUrl,
            phoneNumber,
            type: user.type,
          },
        },
      });

      if (!isStylist) {
        const clientInput: {
          aesthetics: string[];
          bio?: string;
          sizingDetails?: string;
          height?: string;
          shareAge?: boolean;
        } = {
          aesthetics: selectedStylingPreference,
        };

        if (bioData.bio) {
          clientInput.bio = bioData.bio;
        }
        if (clientMeasurements.sizingDetails) {
          clientInput.sizingDetails = clientMeasurements.sizingDetails;
        }
        if (clientMeasurements.height) {
          clientInput.height = clientMeasurements.height;
        }
        if (profileFormData.shareAge !== undefined) {
          clientInput.shareAge = profileFormData.shareAge;
        }

        await updateClient({
          variables: {
            input: clientInput,
          },
        });

        // Check if address changed by comparing current user.address fields to addressData
        const addressChanged = hasAddressChanged(user.address, addressData);

        // Save address if changed and required fields exist
        if (
          addressChanged &&
          addressData.city &&
          addressData.state &&
          addressData.addressLine1 &&
          addressData.zipCode
        ) {
          const streetAddress = [
            addressData.addressLine1,
            addressData.addressLine2,
          ]
            .filter(Boolean)
            .join('\n');

          await updateUserAddress({
            variables: {
              input: {
                streetAddress,
                city: addressData.city,
                state: addressData.state,
                zipCode: addressData.zipCode,
                country: Country.Usa,
                locationType: mapLocationTypeToEnum(addressData.locationType),
                accessDetails: addressData.accessDetails || null,
              },
            },
          });
        }

        return;
      }

      await updateStylist({
        variables: {
          input: {
            bio: bioData.bio,
            landscapeImage: landscapeImageUrl,
            portraitImages: portraitImageUrls,
            expertise: selectedExpertise,
            aesthetics: selectedStylingPreference,
            tiktok: bioData.tikTok,
            instagram: bioData.instagram,
            substack: bioData.substack,
            // TODO: when we enable stylist to update their in-person availability, we will need to use the form data instead of the user prop
            isAvailableInPerson: user?.stylist?.isAvailableInPerson,
          },
        },
      });
    } catch (error) {
      if (error instanceof ApolloError) {
        setSnackbarState({
          open: true,
          message: shouldSurfaceGraphQLError(error)
            ? error.graphQLErrors?.[0]?.message
            : 'There was a problem updating your profile. Please try again.',
          severity: 'error',
        });

        SentryService.captureError('Error saving stylist profile', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Container isDesktop={isDesktop}>
        <IntroContentContainer isDesktop={isDesktop}>
          {!isDesktop && (
            <PageTitle>
              {isStylist ? 'Edit Profile' : `Refresh\nYour Profile`}
            </PageTitle>
          )}
          <StyledBodyText>
            {isStylist
              ? 'Make updates to your profile. Keep the info shown to clients fresh and current.'
              : 'Changed things up? Update your profile so stylists can match you with the best looks.'}
          </StyledBodyText>
          <FlexGroup isDesktop={isDesktop}>
            <ButtonWrapper isDesktop={isDesktop}>
              <Button
                onPress={() => router.push(ROUTES.PREVIEW.path)}
                text="View Profile"
                variant="tertiary"
                size="small"
                customStyle={{
                  width: 165,
                  height: isDesktop ? 45 : 48,
                }}
              />
              <Button
                onPress={saveProfile}
                text="Save Changes"
                disabled={!isChanged || !isFormValid || isUploading}
                loading={isLoading || isUploading}
                customStyle={{
                  width: isDesktop ? 184 : 166,
                  height: isDesktop ? 45 : 48,
                }}
              />
            </ButtonWrapper>
            {isStylist && (
              <RequiredText isDesktop={isDesktop}>*Required</RequiredText>
            )}
          </FlexGroup>
        </IntroContentContainer>
        <FormContainer
          isDesktop={isDesktop}
          paddingBottom={!IS_WEB ? BOTTOM_TAB_BAR_HEIGHT + 46 : undefined}
        >
          <BasicInfoSection
            profileFormData={profileFormData}
            setProfileFormData={setProfileFormData}
            selectImage={selectImage}
          />
          {isStylist && <InPersonSessionSection user={user} />}
          {isStylist ? (
            <>
              <AboutYouSection bioData={bioData} setBioData={setBioData} />
              <ExpertiseSection
                selectedExpertise={selectedExpertise}
                setSelectedExpertise={setSelectedExpertise}
                selectedStylingPreference={selectedStylingPreference}
                setSelectedStylingPreference={setSelectedStylingPreference}
              />
              <PortfolioSection
                galleryImages={galleryImages}
                selectLandscapeImage={selectLandscapeImage}
                selectPortraitImage={selectPortraitImage}
                addPortraitImage={addPortraitImage}
              />
            </>
          ) : (
            <>
              <AboutYouSection bioData={bioData} setBioData={setBioData} />
              <ClientAesthetics
                selectedStylingPreference={selectedStylingPreference}
                setSelectedStylingPreference={setSelectedStylingPreference}
              />
              <MeasurementsSection
                sizingDetails={clientMeasurements.sizingDetails}
                setSizingDetails={(sizingDetails) =>
                  setClientMeasurements((prev) => ({ ...prev, sizingDetails }))
                }
                height={clientMeasurements.height}
                setHeight={(height) =>
                  setClientMeasurements((prev) => ({ ...prev, height }))
                }
                shareAge={profileFormData.shareAge ?? false}
                setShareAge={(shareAge) =>
                  setProfileFormData((prev) => ({ ...prev, shareAge }))
                }
                isDesktop={isDesktop}
              />
            </>
          )}
          <NotificationSection
            profileFormData={profileFormData}
            setProfileFormData={setProfileFormData}
          />
          {!isStylist && (
            <FormSection isDesktop={isDesktop}>
              <SectionHeading isDesktop={isDesktop}>
                IN-PERSON SESSION LOCATION
              </SectionHeading>
              <DescriptionText marginBottom={isDesktop ? 18 : 24}>
                Your location would be used to prefill future in-person session
                requests.
              </DescriptionText>
              <AddressForm user={user} onAddressChange={setAddressData} />
            </FormSection>
          )}
          <BackToTopButton scrollToTop={scrollToTop} />
        </FormContainer>
      </Container>
    </>
  );
};

export default EditProfileForm;
